import React, { useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import moment from 'moment';
import _ from 'lodash';
import { useDebounce } from 'hooks';
import {
  TemplateUsage as TemplateType,
  TemplateRoles as TemplateScope,
} from '@bb/domain/dist/types/Template';

import { Alert } from '@brokerbay/design-components';
import { listNames, Template } from 'api/TemplateController';
import { AuthContext, TeamContext } from 'contexts';
import {
  replaceMarkupWithTags,
  replaceTagsWithMarkup,
  defaultTemplates,
  composeDefaultEmailSignature,
  formatPhoneNumber,
} from 'utils';
import {
  Text,
  Dropdown,
  Menu,
  MenuItem,
  Icon,
  Button,
  FroalaTaggedInput,
  Page,
  SearchBar,
  Modal,
  message,
  Popconfirm,
  Select,
  Tooltip,
} from 'components';

import { Redirect } from 'react-router-dom';
import { UserRoles } from '@bb/users';
import { Loading } from '@app/react-common';
import { FeedbackTemplate } from '@bb/domain/dist/types/Feedback';
import type {
  CreateTemplateRequestBody,
  CreateTemplateResponseBody,
  FindTemplateDefaultResponseBody,
  JsonObject,
  TemplateFindByIdResponse,
  TemplateListAllResponse,
  UpdateTemplateRequestBody,
  UpdateTemplateResponseBody,
} from '@/app-package';
import FilterDropdown from './FilterDropdown';
import ScopeDropdown from './ScopeDropdown';
import TemplateList from './TemplateList';

import {
  AlertContainer,
  DefaultContainer,
  EditorContainer,
  FilterContainer,
  InputGroup,
  LastEditedLabel,
  ListContainer,
  SearchBarContainer,
  StyledInput,
  StyledTemplateLayout,
  SubjectContainer,
  TemplateSettingsActionsContainer,
  TemplateSettingsActionsRow,
  TemplateSettingsContainer,
  StyledSwitch,
} from './StyledComponents';

// laze import so that angular modules have time to be loaded
const FormBuilder = React.lazy(
  () => import('@app/listing/appointments/feedback/FormBuilder'),
);

const DEBOUNCE_INTERVAL = 500;

type Extends<T, U extends T> = U;
type RootDefaultTemplateType = Extends<
  TemplateType,
  'feedback' | 'feedbackSurvey' | 'offer' | 'emailSignature'
>;

const ROOT_DEFAULT_TEMPLATES: Record<RootDefaultTemplateType, Template> = {
  feedback: {
    isDefault: true,
    id: 'rootDefault-feedback',
    type: 'feedback',
    name: 'Default Feedback template',
    role: 'GLOBAL',
  },
  // TODO: Uncomment for usage
  // propertyAnnouncement: {
  //   isDefault: true,
  //   id: 'rootDefault-propertyAnnouncement',
  //   type: 'propertyAnnouncement',
  //   name: 'Default Message Showing Agents template',
  // },
  feedbackSurvey: {
    isDefault: true,
    id: 'rootDefault-feedbackSurvey',
    type: 'feedbackSurvey',
    name: 'Default Feedback Survey',
    role: 'GLOBAL',
  },
  offer: {
    isDefault: true,
    id: 'rootDefault-offer',
    type: 'offer',
    name: 'Default Offer Registration template',
    role: 'GLOBAL',
  },
  emailSignature: {
    isDefault: true,
    id: 'rootDefault-emailSignature',
    type: 'emailSignature',
    name: 'Default Email Signature template',
    role: undefined,
  },
} as Record<RootDefaultTemplateType, Template>;

const SCOPE_PRIORITY = { GLOBAL: 4, BRANCH: 3, TEAM: 2, USER: 1 };

const FEEDBACK_LINK_DATA_TYPE = 'data-type="feedbackLink"';

const TAG_ERROR_MESSAGE = 'Template must include the Feedback Button Link tag';

const getTemplateScope = (template: {
  user?: string;
  team?: string;
  location?: string[];
}) => {
  if (template.user) return 'USER';
  if (template.team) return 'TEAM';
  if (template.location?.length) return 'BRANCH';
  return 'GLOBAL';
};

interface TemplateState {
  id?: string;
  updatedAt?: Date;

  name?: string;
  type?: TemplateType;
  isDefault?: boolean;

  subject?: string;
  body?: string;

  scope?: { location?: string[]; team?: string; isOwnTemplate?: boolean };
  // For Root Default Name Overlap, this is the `rootDefault-type`
  isRoot?: string;
}

const TemplateManager = () => {
  /**
   * Props & state
   */

  const { auth, hasFeature, hasLimitedAccess } = useContext(AuthContext);
  const { availableTeamStates } = useContext(TeamContext);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateNames, setTemplateNames] =
    useState<Record<TemplateType, string[]>>();
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1); // use for adding pagination to templates
  const [hasMore, setHasMore] = useState<boolean>(true);

  const [editing, setEditing] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [popconfirmVisiblity, setPopconfirmVisibility] =
    useState<boolean>(false);

  // State of the Create Template dropdown UI
  const [createTemplateOpen, setCreateTemplateOpen] = useState<boolean>(false);
  // State of the Branch dropdown UI
  const [branchOpen, setBranchOpen] = useState<boolean>(false);
  const [branchFilter, setBranchFilter] = useState<string>('all');
  // State of the Type dropdown UI
  const [typeOpen, setTypeOpen] = useState<boolean>(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  // State of the Sort dropdown UI
  const [sortOpen, setSortOpen] = useState<boolean>(false);
  const [sortFilter, setSortFilter] = useState<string>('new');
  // State of the Scope dropdown UI
  const [scopeOpen, setScopeOpen] = useState<boolean>(false);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_INTERVAL);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  const availableTeams = useMemo(() => {
    const teamIds = auth.teams.map(id => id.toString());
    return availableTeamStates?.filter(({ id }) => teamIds.includes(id)) ?? [];
  }, [auth.teams, availableTeamStates]);

  const fetchTemplates = async () => {
    setLoading(true);
    const { data } = await axios.get<Template[]>(`/template/list`, {
      params: {
        meta: true,
        sort: sortFilter,
        branch: branchFilter,
        type: typeFilter,
        search: debouncedSearchTerm,
        page,
      },
    });
    if (data.length < 20) setHasMore(false);
    if (page === 1) setTemplates(data);
    else setTemplates(prev => [...prev, ...data]);
    setLoading(false);
  };

  useEffect(() => {
    const fetchNames = async () => {
      try {
        const nameMap = await listNames();
        setTemplateNames(nameMap);
      } catch (err) {
        message.error('Error loading template names');
      }
    };

    fetchNames();
    return () => Modal.destroyAll();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [page]);

  useEffect(() => {
    setTemplates([]);
    setHasMore(true);
    if (page !== 1) setPage(1);
    else fetchTemplates();
  }, [sortFilter, branchFilter, typeFilter, debouncedSearchTerm]);

  useEffect(() => {
    // adding any missing default template after all templates are loaded
    if (!hasMore && !debouncedSearchTerm) {
      const data: Template[] = [];
      Object.entries(ROOT_DEFAULT_TEMPLATES).forEach(
        ([type, defaultTemplate]) => {
          if (
            !templates.find(
              t =>
                t.type === type &&
                (getTemplateScope(t) === 'GLOBAL' ||
                  type === 'emailSignature') &&
                t.isDefault,
            ) &&
            (typeFilter === 'all' || type === typeFilter)
          ) {
            data.push(defaultTemplate);
          }
        },
      );

      if (data.length) setTemplates(prev => [...prev, ...data]);
    }
  }, [hasMore, templates, debouncedSearchTerm, typeFilter]);

  const loadMoreTemplates = () => setPage(b => b + 1);

  const getTemplate = async (id?: string) => {
    if (!id) return;
    const { data } = await axios.get<TemplateFindByIdResponse>(
      `/template/${id}`,
    );
    return data;
  };

  const [activeTemplate, setActiveTemplate] = useState<
    TemplateState | undefined
  >();
  const [activeTemplateCopy, setActiveTemplateCopy] = useState<
    TemplateState | undefined
  >();

  const isInvalidSurvey = useMemo(() => {
    if (!activeTemplate) return false;
    if (!activeTemplate.body) return false;
    if (activeTemplate.type !== 'feedbackSurvey') return false;
    try {
      const jsonBody = JSON.parse(activeTemplate.body);
      if (jsonBody && _.isArray(jsonBody) && jsonBody.length === 0) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }, [activeTemplate]);

  const activeTemplateScope = useMemo(() => {
    if (!activeTemplate?.scope) return undefined;
    if (activeTemplate.scope.isOwnTemplate) return 'USER';
    if (activeTemplate.scope.team) return 'TEAM';
    if (activeTemplate.scope.location?.length) return 'BRANCH';
    return 'GLOBAL';
  }, [activeTemplate]);

  const editAuthRole = useMemo(() => {
    if (!activeTemplate?.scope) return UserRoles['Global Admin'];
    if (activeTemplate.scope.isOwnTemplate) return UserRoles.Agent;
    if (activeTemplate.scope.team) return UserRoles['Team Admin'];
    if (activeTemplate.scope.location?.length) return UserRoles['Branch Admin'];
    return UserRoles['Global Admin'];
  }, [activeTemplate]);

  const nameError = useMemo(() => {
    if (activeTemplate?.isRoot) return false;
    // Can't be empty
    if (!activeTemplate?.name?.trim()) {
      return 'Please enter a unique name for your template';
    }

    const initialName = templates.find(t => t.id === activeTemplate.id)?.name;

    if (initialName && initialName === activeTemplate.name) {
      return false;
    }

    if (templateNames?.[activeTemplate.type]?.includes(activeTemplate.name)) {
      return 'This template name already exists, please enter a unique name';
    }

    return false;
  }, [templates, templateNames, activeTemplate]);

  const teamsError = useMemo(() => {
    if (!editing) return null;

    if (activeTemplate?.scope?.team && !selectedTeams.length) {
      return 'At least one team must be selected';
    }

    return null;
  }, [activeTemplate, selectedTeams.length, editing]);

  const tagError = useMemo(() => {
    if (
      activeTemplate?.type === 'feedback' &&
      !activeTemplate?.body?.includes(FEEDBACK_LINK_DATA_TYPE)
    )
      return true;

    return false;
  }, [activeTemplate]);

  const isTeamScopeAndLackTeams =
    activeTemplateScope === 'TEAM' && !selectedTeams.length;

  useEffect(() => {
    setPreviewing(false);
  }, [activeTemplate, editing]);

  const createTemplateMenuItems: { label: string; value: TemplateType }[] = [
    { label: 'Feedback Request', value: 'feedback' as TemplateType },
    { label: 'Feedback Survey', value: 'feedbackSurvey' as TemplateType },
    {
      label: 'Message Showing Agents',
      value: 'propertyAnnouncement' as TemplateType,
    },
    hasFeature('OFFERS') && {
      label: 'Offer Registration',
      value: 'offer' as TemplateType,
    },
    // Email Signature shouldn't be newly created
    // { label: 'Email Signature', value: 'emailSignature' },
    // This was crossed out in the designs
    // { label: 'Send a Listing', value: 'sendListing' },
  ].filter(Boolean);

  const typeMenuItems = [
    { label: 'All Types', value: 'all' },
    ...createTemplateMenuItems,
  ];
  const sortMenuItems = [
    { label: 'Newest', value: 'new' },
    { label: 'A→Z', value: 'az' },
    { label: 'Z→A', value: 'za' },
    { label: 'Oldest', value: 'old' },
  ];

  const alertLabels: Record<
    TemplateType,
    { title: string; description: string }
  > = {
    feedback: {
      title: 'This is a Feedback template.',
      description:
        'Feedback templates are delivered to showing agents following their showing asking them to provide feedback.',
    },
    feedbackSurvey: {
      title: 'This is a Feedback template.',
      description:
        'Feedback templates are delivered to showing agents following their showing asking them to provide feedback.',
    },
    offer: {
      title: 'This is an Offer Registration template.',
      description:
        'Offer Registration templates are delivered to showing agents when an offer is registered on a listing.',
    },
    propertyAnnouncement: {
      title: 'This is a Message Showing Agent template.',
      description:
        'Message Showing Agent templates can be delivered to showing agents on a listing when a message is sent through the “Message Showing Agent” feature.',
    },
    emailSignature: {
      title: 'This is your Email Signature.',
      description:
        'Your email Signature will appear at the bottom of most messages sent on behalf of you through the system.',
    },
    sendListing: {
      title: 'This is a Send Listing template.',
      description:
        'Send Listing templates can be delivered to users through the Send Listing" feature.',
    },
  };

  const isAuthorizedFor = (role: UserRoles) =>
    auth?.roles[auth?.role] >= auth?.roles[role];

  const isRole = (role: UserRoles) => auth?.role === role;

  /**
   * Actions
   */

  const getDefaultTemplate = async (
    type: TemplateType,
    fetchRoot?: boolean,
  ): Promise<{ body: string; subject?: string; name?: string }> => {
    // Currently always used with fetchRoot true, but might have use-case later on
    if (!fetchRoot) {
      const { data } = await axios.get<FindTemplateDefaultResponseBody>(
        `/template/default?type=${type}`,
      );

      if (data) return data;
    }

    // Fallback to root defaults
    if (type === 'emailSignature') {
      const {
        location,
        org,
        firstName,
        lastName,
        address,
        phone,
        frontEndInstructions,
        email,
      } = auth;
      const options = {
        brokerageName:
          org.addresses?.[Number(location)]?.[Number(address)]?.name ||
          org.name,
        agentName: `${firstName} ${lastName}`,
        officeNumber: formatPhoneNumber(
          org.addresses?.[Number(location)]?.[Number(address)]?.phone,
        ),
        mobileNumber: formatPhoneNumber(
          frontEndInstructions?.releaseCell && phone,
        ),
        email,
      };
      return composeDefaultEmailSignature(options);
    }
    return defaultTemplates[type as Exclude<TemplateType, 'emailSignature'>];
  };

  const setTemplateScope = (scope: TemplateScope) => {
    if (!auth) return;

    switch (scope) {
      case 'BRANCH':
        setActiveTemplate(
          cur =>
            cur && {
              ...cur,
              scope: {
                isOwnTemplate: false,
                location: _.uniq([auth.location, ...(auth.locations || [])]),
              },
            },
        );
        break;
      case 'TEAM':
        setActiveTemplate(
          cur =>
            cur && {
              ...cur,
              // set location empty so it doesn't influence searching by branch
              // obs: scope.team will be replaced whenever selectedTeams are changed
              scope: {
                isOwnTemplate: false,
                team: auth.team!.toString(),
                location: [],
              },
            },
        );
        break;
      case 'USER':
        setActiveTemplate(
          cur =>
            cur && {
              ...cur,
              // set location empty so it doesn't influence searching by branch
              scope: { isOwnTemplate: true, location: [] },
            },
        );
        break;
      case 'GLOBAL':
      default:
        setActiveTemplate(cur => cur && { ...cur, scope: { location: [] } });
        break;
    }
  };

  const setDefault = async (val: boolean) => {
    // Cannot undefault a global default directly, as it may
    // lead to a case where there's no default at all
    if (
      !val &&
      templates.find(t => t.id === activeTemplate?.id)?.isDefault &&
      activeTemplateScope === 'GLOBAL'
    )
      return;

    setActiveTemplate(cur => cur && { ...cur, isDefault: val });
  };

  const promptDiscard = (onConfirm: () => void) => {
    const { confirm } = Modal;

    confirm({
      title: 'Leaving will delete any unsaved progress. Continue?',
      // @ts-ignore
      icon: <Icon type="antdQuestionCircle" width={22} color="answering" />,
      okText: 'OK',
      cancelText: 'Cancel',
      autoFocusButton: 'cancel',
      okButtonProps: { className: 'ant-btn-warning' },
      onOk() {
        onConfirm();
      },
      onCancel() {
        // Do nothing
      },
    });
  };

  const newTemplate = (
    type: TemplateType,
    options?: {
      beginEditing?: boolean;
      rootDefault?: boolean;
      isNew?: boolean;
    },
  ) => {
    const { beginEditing = true, rootDefault, isNew } = options || {};

    const proceed = async () => {
      const defaultTemplate = await getDefaultTemplate(
        type,
        rootDefault || isNew,
      );

      const newActiveTemplate = {
        name: !isNew && defaultTemplate?.name ? defaultTemplate.name : '',
        type,
        scope:
          type === 'emailSignature'
            ? {
                // emailSignature can only be created for user
                isOwnTemplate: true,
                location: [auth?.location],
              }
            : {
                // New templates will be scoped to self by default for licensed
                // agents, or to branch/team for admins. Global scope is implied
                // for global admins who fit none of these criteria
                isOwnTemplate: !rootDefault && auth.isLicensed,
                location:
                  !rootDefault &&
                  !auth.isLicensed &&
                  isRole(UserRoles['Branch Admin'])
                    ? [auth.location, ...(auth.locations || [])]
                    : [],
                team:
                  !rootDefault &&
                  !auth.isLicensed &&
                  isRole(UserRoles['Team Admin'])
                    ? auth.team?.toString()
                    : undefined,
              },
        subject: defaultTemplate?.subject || '',
        body:
          type === 'feedbackSurvey'
            ? defaultTemplate.body
            : replaceTagsWithMarkup(defaultTemplate?.body) || '',
        // Only default a new template if it's from the root default item
        isDefault: rootDefault,
        isRoot: rootDefault && `rootDefault-${type}`,
      };

      /* teams are trimmed when saving/updating on non-team scope */
      const [usersFirstTeam] = availableTeams;
      if (usersFirstTeam) setSelectedTeams([usersFirstTeam.id]);

      setEditing(false);
      setActiveTemplate(newActiveTemplate);
      setActiveTemplateCopy(newActiveTemplate);

      // https://brokerbay.atlassian.net/browse/APP-3212
      // Set `editing` to true after new template model is passed in to Froala Editor
      // It will prevent the editor from triggering onModelChange with the modified model
      await Promise.resolve();
      if (beginEditing || editing) setEditing(true);
    };

    // Check if data has changed
    const modified = !_.isEqual(activeTemplate, activeTemplateCopy);
    // Make sure it's safe to discard current data and continue
    // If it is new default loading, prevent double prompt
    if (activeTemplate?.type && editing && !rootDefault && modified) {
      promptDiscard(proceed);
    } else {
      proceed();
    }
  };

  const loadTemplate = (
    id?: string,
    template?: TemplateFindByIdResponse,
    options?: { beginEditing?: boolean },
  ) => {
    const { beginEditing = false } = options || {};
    const proceed = async () => {
      setEditing(false);

      // Handle root default template case
      if (id?.startsWith('rootDefault')) {
        const [, type] = id.split('rootDefault-');
        newTemplate(type as TemplateType, {
          beginEditing: false,
          rootDefault: true,
        });
      } else {
        const {
          name,
          location,
          team,
          user,
          isDefault,
          subject,
          body,
          type,
          updatedAt,
          role,
          teams,
        } = template || (await getTemplate(id)) || {};

        const newActiveTemplate = {
          id: id || template?._id.toString(),
          name,
          isDefault,
          subject,
          body: type === 'feedbackSurvey' ? body : replaceTagsWithMarkup(body),
          type,
          updatedAt: new Date(updatedAt),
          scope: {
            location: role !== 'GLOBAL' ? location : [],
            team: role === 'TEAM' ? team : undefined,
            isOwnTemplate: role === 'USER' && user === auth.id,
          },
        };

        let teamsToSet: string[] = [];
        if (newActiveTemplate.scope.team) {
          const templateTeams =
            teams?.map((teamId: any) => teamId.toString()) || [];
          const [usersFirstTeam] = availableTeams;

          // set template current team
          if (templateTeams.length) {
            teamsToSet = templateTeams;
          }

          // or use user's primary team
          else if (usersFirstTeam) {
            teamsToSet = [usersFirstTeam.id];
          }
        }

        setSelectedTeams(teamsToSet);
        setActiveTemplate(newActiveTemplate);
        setActiveTemplateCopy(newActiveTemplate);
        if (beginEditing) setEditing(true);
      }
    };

    // Check if data has changed
    const modified = !_.isEqual(activeTemplate, activeTemplateCopy);
    // Make sure it's safe to discard current data and continue
    // If a 'template' is given, instead of its id to be fetched,
    // Just proceed since it is from newly created template loading
    if (activeTemplate?.type && editing && !template && modified) {
      promptDiscard(proceed);
    } else {
      proceed();
    }
  };

  // Load the first template if nothing else is loaded
  useEffect(() => {
    if (auth && templates?.length && !activeTemplate)
      loadTemplate(templates[0].id || templates[templates.length - 1].id);
  }, [auth, templates, activeTemplate]);

  // update selected primary team when selectedTeams is changed
  useEffect(() => {
    if (activeTemplate?.scope?.team && selectedTeams.length) {
      const [primaryTeam] = selectedTeams;
      setActiveTemplate(
        cur => cur && { ...cur, scope: { team: primaryTeam, location: [] } },
      );
    }
  }, [activeTemplate?.scope?.team, selectedTeams]);

  const saveTemplate = async () => {
    const createTemplate = async (data: TemplateState) => {
      const template: CreateTemplateRequestBody = {
        name: data.name?.trim(),
        subject: data.subject,
        body:
          data.type === 'feedbackSurvey'
            ? data.body
            : replaceMarkupWithTags(data.body),
        type: data.type,
        isDefault: Boolean(data.isDefault),

        // Handle scopes
        isOwnTemplate: data.scope.isOwnTemplate,
        location: data.scope.location,
        team: data.scope.team,
        ...(activeTemplateScope === 'TEAM' ? { teams: selectedTeams } : {}),
      };

      const { data: savedData } = await axios.post<
        JsonObject<CreateTemplateResponseBody>
      >('/template', template);

      // Refresh template list
      await fetchTemplates();
      loadTemplate(undefined, savedData);
    };

    const updateTemplate = async (data: TemplateState) => {
      if (!data.id) throw new Error('Cannot update a template with no id');
      const template: UpdateTemplateRequestBody = {
        name: data.name?.trim(),
        subject: data.subject,
        body:
          data.type === 'feedbackSurvey'
            ? data.body
            : replaceMarkupWithTags(data.body),
        isDefault: Boolean(data.isDefault),
        // Handle scopes
        location: data.scope.location,
        isOwnTemplate: data.scope.isOwnTemplate,
        team: data.scope.team,
        ...(activeTemplateScope === 'TEAM' ? { teams: selectedTeams } : {}),
      };

      await axios.post<UpdateTemplateResponseBody>(
        `/template/${data.id}`,
        template,
      );
      // Refresh template list
      await fetchTemplates();
    };

    if (nameError) return;

    setEditing(false);

    if (activeTemplate.id) {
      await updateTemplate(activeTemplate);
    } else {
      await createTemplate(activeTemplate);
    }

    message.success({ content: 'Template Saved', duration: 2 });
  };

  const onClickDisabledInput = (text: string) => {
    message.warning({
      content: (
        <Text>
          Click <Text strong>{text}</Text> to make changes.
        </Text>
      ),
    });
  };

  const duplicateTemplate = async () => {
    // returns max copy number
    const copyNumber = templates.reduce((maxNum, template) => {
      if (template.name.includes(`${activeTemplate.name} copy`)) {
        const existingTemplateCopyNumber = Number(
          template.name.split(`${activeTemplate.name} copy`)[1],
        );
        if (existingTemplateCopyNumber) {
          return Math.max(existingTemplateCopyNumber + 1, maxNum);
        }
        return Math.max(maxNum, 2);
      }
      return maxNum;
    }, 1);

    const copy: CreateTemplateRequestBody = {
      name: `${activeTemplate.name} copy${
        copyNumber > 1 ? ` ${copyNumber}` : ''
      }`.trim(),
      subject: activeTemplate.subject,
      body:
        activeTemplate.type === 'feedbackSurvey'
          ? activeTemplate.body
          : replaceMarkupWithTags(activeTemplate.body),
      type: activeTemplate.type,
      isDefault: false,

      // Duplicates will be scoped to self by default for licensed agents,
      // or to branch/team for admins. Global scope is implied for global admins
      // who fit none of these criteria
      isOwnTemplate: auth.isLicensed,
      location: isRole(UserRoles['Branch Admin'])
        ? [auth.location, ...(auth.locations || [])]
        : undefined,
      team: isRole(UserRoles['Team Admin']) ? auth.team?.toString() : undefined,
    };

    const { data } = await axios.post<JsonObject<CreateTemplateResponseBody>>(
      '/template',
      copy,
    );
    // Refresh template list and set the new dup as active
    await fetchTemplates();
    loadTemplate(undefined, data, { beginEditing: true });
  };

  const deleteTemplate = async () => {
    const proceed = async () => {
      setDeleting(true);

      // There should be always a GLOBAL (ROOT GLOBAL) template
      const isGlobal =
        activeTemplateScope === 'GLOBAL' ||
        activeTemplate.type === 'emailSignature';
      // Cannot delete default global template
      if (activeTemplate.isDefault && isGlobal) return;

      // Can't delete a template that hasn't been created yet
      if (!activeTemplate.id) return;

      await axios.delete(`/template/${activeTemplate.id}`);
      // Refresh template list
      await fetchTemplates();
      setActiveTemplate(undefined);
      setDeleting(false);
    };

    setEditing(false);

    const { confirm } = Modal;
    confirm({
      title: 'Are you sure you want to delete this template?',
      icon: <Icon type="antdExclamationCircle" width={22} color="answering" />,
      okText: 'Delete',
      cancelText: 'Cancel',
      autoFocusButton: null,
      okButtonProps: { danger: true },
      onOk() {
        proceed();
      },
      onCancel() {
        setEditing(true);
      },
    });
  };

  const discardUnsavedTemplate = () => {
    if (templates.length) loadTemplate(templates[0].id);
  };

  const hideTrashCan = useMemo(() => {
    // If it is a root default, it can't be manually deleted
    if (activeTemplate?.isRoot) return true;
    // If it's a new template it can be safely discarded
    if (!activeTemplate?.id) return false;

    // There should be always a GLOBAL (ROOT GLOBAL) template
    const isGlobal =
      activeTemplateScope === 'GLOBAL' ||
      activeTemplate.type === 'emailSignature';
    return deleting || (activeTemplate?.isDefault && isGlobal);
  }, [deleting, activeTemplate, activeTemplateScope]);

  const canApplyToTeam = useMemo(() => {
    const isAuthorized = isAuthorizedFor(UserRoles['Team Admin']);
    const hasTeamOrTeams = auth.team || auth.teams.length;

    return (isAuthorized || hasTeamOrTeams) && hasFeature('MY_BROKERAGE');
  }, [activeTemplate, auth.team, auth.teams, hasFeature, isAuthorizedFor]);

  /**
   * Render
   */

  if (!auth) return <></>;

  if (!auth.isLicensed) {
    if (auth.roles[auth.role] === auth.roles.AGENT) {
      return <Redirect to="/feature-missing" />;
    }
    if (!hasFeature('MY_BROKERAGE')) {
      return <Redirect to="/feature-missing" />;
    }
  }

  const branchMenuItems = [
    { label: 'All Branches', value: 'all' },
    ...(auth.org.locations || [])
      .filter(l => !l.isDeleted)
      .map((l, idx) => ({ label: l.name, value: idx.toString(10) })),
  ];

  const templateSubject = () => {
    switch (activeTemplate?.type) {
      case 'feedback':
        return "Showing Feedback - 'Listing Address'";
      case 'offer':
        return "NEW REGISTERED OFFER (# TOTAL) - 'Listing Address'";
      default:
        return activeTemplate?.subject || 'Subject';
    }
  };

  return (
    <Page
      overAlertVariant={hasLimitedAccess ? 'noAccess' : undefined}
      title="Template Manager"
      type="filled"
      headerExtra={[
        <Dropdown
          key="createTemplate"
          disabled={hasLimitedAccess}
          trigger={['click']}
          open={createTemplateOpen}
          onOpenChange={visible => setCreateTemplateOpen(visible)}
          dropdownRender={() => (
            <Menu>
              {createTemplateMenuItems.map(
                ({ label, value }: any, idx: number) => (
                  <MenuItem
                    key={idx}
                    onClick={async () => {
                      setCreateTemplateOpen(false);
                      newTemplate(value, { isNew: true });
                    }}
                  >
                    {label}
                  </MenuItem>
                ),
              )}
            </Menu>
          )}
          overlayStyle={{ minWidth: '12em' }}
          placement="bottomRight"
        >
          <Button type="primary" data-cy="create-template-button">
            Create Template
            <Icon
              flipped={createTemplateOpen}
              type="arrowDownThin"
              width={12}
              height={8}
              color="white"
              offset={[0, -2]}
            />
          </Button>
        </Dropdown>,
      ]}
    >
      <StyledTemplateLayout $blur={hasLimitedAccess}>
        <ListContainer>
          <SearchBarContainer>
            <SearchBar
              value={searchTerm}
              placeholder="Search templates"
              onChange={value => setSearchTerm(value)}
              loading={loading}
              shape="round"
              dataCy="template-search-templates"
            />
          </SearchBarContainer>
          <FilterContainer>
            {isAuthorizedFor(UserRoles['Branch Admin']) &&
              hasFeature('MY_BROKERAGE') && (
                <FilterDropdown
                  value={branchFilter}
                  open={branchOpen}
                  setOpen={setBranchOpen}
                  menuItems={branchMenuItems}
                  onChange={setBranchFilter}
                  dataCy={'template-select-branch'}
                />
              )}
            <FilterDropdown
              value={typeFilter}
              open={typeOpen}
              setOpen={setTypeOpen}
              menuItems={typeMenuItems}
              onChange={setTypeFilter}
              dataCy={'template-select-type'}
            />
            <FilterDropdown
              value={sortFilter}
              open={sortOpen}
              setOpen={setSortOpen}
              menuItems={sortMenuItems}
              onChange={setSortFilter}
              dataCy={'template-select-sort'}
            />
          </FilterContainer>
          <TemplateList
            items={templates}
            selected={activeTemplate?.id || activeTemplate?.isRoot}
            onChangeSelection={id => loadTemplate(id)}
            auth={auth}
            loadMoreTemplates={loadMoreTemplates}
            loading={loading}
            hasMore={hasMore}
          />
        </ListContainer>

        {activeTemplate?.type && (
          <EditorContainer>
            <TemplateSettingsContainer>
              <TemplateSettingsActionsRow>
                <InputGroup
                  stretch
                  onClick={() => {
                    if (!editing) onClickDisabledInput('Edit');
                  }}
                >
                  <StyledInput
                    label="Template Name"
                    value={activeTemplate?.name}
                    onChange={e => {
                      const name = e.target.value;
                      setActiveTemplate(cur => cur && { ...cur, name });
                    }}
                    id="templateName"
                    data-cy="template-edit-name"
                    disabled={!editing}
                    error={editing && nameError}
                    position="after"
                  />
                </InputGroup>
                {editing || deleting ? (
                  <>
                    {activeTemplate.type !== 'emailSignature' &&
                      !hideTrashCan && (
                        <InputGroup
                          marginBottom={Boolean(nameError)}
                          key="delete"
                        >
                          <Button
                            danger
                            onClick={
                              activeTemplate?.id
                                ? deleteTemplate
                                : discardUnsavedTemplate
                            }
                            icon={
                              <Icon type="delete" width={15} color={'danger'} />
                            }
                            shape="circle"
                            aria-label="delete"
                            data-cy="delete-button"
                          />
                        </InputGroup>
                      )}
                    <InputGroup marginBottom={Boolean(nameError)} key="save">
                      <Tooltip
                        title={
                          isInvalidSurvey
                            ? 'Must include at least one survey question'
                            : ''
                        }
                        placement="bottomLeft"
                      >
                        <div>
                          <Button
                            type="primary"
                            onClick={saveTemplate}
                            disabled={Boolean(
                              tagError ||
                                nameError ||
                                !activeTemplate?.body ||
                                isTeamScopeAndLackTeams ||
                                isInvalidSurvey,
                            )}
                            data-cy="save-button"
                          >
                            Save
                          </Button>
                        </div>
                      </Tooltip>
                    </InputGroup>
                  </>
                ) : (
                  <>
                    {activeTemplate.type !== 'emailSignature' && (
                      <InputGroup key="duplicate">
                        <Button
                          type="default"
                          onClick={duplicateTemplate}
                          data-cy={'duplicate-button'}
                        >
                          Duplicate
                        </Button>
                      </InputGroup>
                    )}
                    <InputGroup key="edit">
                      <Button
                        type="primary"
                        onClick={() => setEditing(true)}
                        disabled={!isAuthorizedFor(editAuthRole)}
                        data-cy={'edit-button'}
                      >
                        Edit
                      </Button>
                    </InputGroup>
                  </>
                )}
              </TemplateSettingsActionsRow>
              {Boolean(activeTemplate.updatedAt) && (
                <LastEditedLabel>
                  Last edited:{' '}
                  {moment(activeTemplate.updatedAt).format('MMMM D, h:mma')}
                </LastEditedLabel>
              )}
              <TemplateSettingsActionsRow>
                {activeTemplate.type !== 'emailSignature' &&
                  Boolean(canApplyToTeam) && (
                    <>
                      <InputGroup marginBottom={Boolean(teamsError)}>
                        <label>
                          Apply to
                          <ScopeDropdown
                            value={activeTemplateScope!}
                            onChange={(value: TemplateScope) =>
                              setTemplateScope(value)
                            }
                            open={scopeOpen}
                            setOpen={setScopeOpen}
                            // Cannot change scope of a template if it's the default for another scope
                            disabled={!editing || activeTemplate.isDefault}
                            templateType={activeTemplate.type}
                          />
                        </label>
                      </InputGroup>
                      {auth.teams.length > 1 &&
                        activeTemplateScope === 'TEAM' && (
                          <InputGroup>
                            <label>
                              Select team(s):
                              <Select
                                value={selectedTeams}
                                onChange={setSelectedTeams}
                                mode="multiple"
                                placeholder="Select team(s)..."
                                optionFilterProp="label"
                                style={{ minWidth: '350px' }}
                                error={teamsError}
                                position="after"
                                options={availableTeams.map((team, index) => ({
                                  key: index,
                                  value: team.id,
                                  label: team.name,
                                }))}
                                disabled={!editing || activeTemplate.isDefault}
                              />
                            </label>
                          </InputGroup>
                        )}
                    </>
                  )}
                <DefaultContainer
                  emptyAbove={
                    !(
                      isAuthorizedFor(UserRoles['Team Admin']) &&
                      hasFeature('MY_BROKERAGE')
                    )
                  }
                >
                  <InputGroup
                    marginBottom={Boolean(teamsError)}
                    style={{ display: 'flex' }}
                  >
                    <StyledSwitch
                      checked={!!activeTemplate.isDefault}
                      onChange={val => {
                        if (!val) {
                          setDefault(val);
                        } else {
                          setPopconfirmVisibility(true);
                        }
                      }}
                      disabled={
                        !editing ||
                        Boolean(
                          activeTemplate.id &&
                            templates.find(t => t.id === activeTemplate.id)
                              ?.isDefault &&
                            activeTemplateScope === 'GLOBAL',
                        )
                      }
                      data-cy={'template-toggle-make-default'}
                    />{' '}
                    {activeTemplate.isDefault
                      ? 'Default template'
                      : 'Make default template'}
                  </InputGroup>

                  <Popconfirm
                    title={
                      <span
                        data-cy={`default-popup${
                          activeTemplateScope && activeTemplateScope !== 'USER'
                            ? `-${activeTemplateScope.toLowerCase()}`
                            : ''
                        }`}
                      >
                        {`Are you sure you want to make this template a ${
                          activeTemplateScope && activeTemplateScope !== 'USER'
                            ? `${activeTemplateScope.toLowerCase()} `
                            : ''
                        }default?`}
                      </span>
                    }
                    open={popconfirmVisiblity}
                    onCancel={() => setPopconfirmVisibility(false)}
                    onConfirm={() => {
                      setDefault(true);
                      setPopconfirmVisibility(false);
                    }}
                    okText="Yes"
                    cancelText="No"
                    placement="bottomRight"
                  />
                </DefaultContainer>
              </TemplateSettingsActionsRow>
            </TemplateSettingsContainer>

            <AlertContainer data-cy={'template-information-container'}>
              {/* TODO: Alert colors */}
              <Alert
                type="info"
                showIcon
                message={
                  <>
                    <strong>{alertLabels[activeTemplate.type]?.title}</strong>{' '}
                    {alertLabels[activeTemplate.type]?.description}
                  </>
                }
                dataCy={'template-information-content'}
              />
            </AlertContainer>
            {activeTemplate.type === 'feedbackSurvey' ? (
              <React.Suspense fallback={<Loading size={'fa-3x'} />}>
                {/* allow angular style to kick in */}
                <div className={'global--angular'} key={`${editing}`}>
                  <FormBuilder
                    template={(bodyString => {
                      try {
                        return JSON.parse(bodyString);
                      } catch (e) {
                        message.error(
                          `Error processing feedback survey template ${activeTemplate.name}`,
                        );
                        return [];
                      }
                    })(activeTemplate.body)}
                    updateTemplate={template =>
                      setActiveTemplate(
                        cur =>
                          cur && { ...cur, body: JSON.stringify(template) },
                      )
                    }
                    type={'global'}
                    isPreview={!editing}
                    notEditable
                  />
                </div>
              </React.Suspense>
            ) : (
              <>
                <SubjectContainer>
                  {/* Some template types' subjects shouldn't be editable */}
                  {activeTemplate &&
                    activeTemplate.type !== 'emailSignature' && (
                      <InputGroup stretch>
                        <StyledInput
                          label="Subject"
                          value={activeTemplate?.subject}
                          onChange={e => {
                            const subject = e.target.value;
                            setActiveTemplate(
                              cur => cur && { ...cur, subject },
                            );
                          }}
                          id="subject"
                          disabled={
                            !editing ||
                            activeTemplate.type !== 'propertyAnnouncement'
                          }
                          placeholder={templateSubject()}
                          data-cy={'template-edit-subject'}
                        />
                      </InputGroup>
                    )}
                  <Button
                    data-cy={'template-preview-button'}
                    onClick={() => setPreviewing(!previewing)}
                    strong
                  >
                    {previewing ? 'Exit Preview' : 'Preview'}
                  </Button>
                </SubjectContainer>
                <FroalaTaggedInput
                  model={activeTemplate?.body || ''}
                  onModelChange={(model: string) =>
                    setActiveTemplate(cur => cur && { ...cur, body: model })
                  }
                  templateType={activeTemplate.type}
                  disabled={!editing}
                  onClick={() => {
                    if (!editing) {
                      onClickDisabledInput('Edit');
                    } else if (previewing) {
                      onClickDisabledInput('Exit Preview');
                    }
                  }}
                  preview={previewing}
                  hasFeature={hasFeature}
                  dataCy={'template-content-area'}
                  errorMessage={tagError && TAG_ERROR_MESSAGE}
                />
              </>
            )}
          </EditorContainer>
        )}
      </StyledTemplateLayout>
    </Page>
  );
};

export default TemplateManager;
